import React, { useEffect, useMemo, useState } from "react";
import {
  Bell, BellOff, Check, Download, Eye, KeyRound,
  RefreshCw, Search, TrendingDown, TrendingUp,
  UserPlus, Users, UserX, Wallet,
} from "lucide-react";
import API from "../services/api";
import { getMediaUrl } from "../services/api";
import AdminLayout from "./AdminLayout";
import { useNavigate } from "react-router-dom";

/* ─── colour palette ─── */
const C = {
  brand:  "#4f46e5",
  brand2: "#7c3aed",
  orange: "#f97316",
  orange2:"#ea580c",
  dark:   "#0f172a",
  dark2:  "#1e1b4b",
  green:  "#16a34a",
  red:    "#dc2626",
  slate:  "#64748b",
  light:  "#f1f5f9",
};

/* ─── avatar palette ─── */
const AVATAR_COLORS = [
  ["#4f46e5","#7c3aed"], ["#0891b2","#0e7490"], ["#059669","#047857"],
  ["#d97706","#b45309"], ["#dc2626","#b91c1c"], ["#7c3aed","#6d28d9"],
  ["#0284c7","#0369a1"], ["#be185d","#9d174d"],
];

function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers]   = useState([]);
  const [agents, setAgents] = useState([]);
  const [query, setQuery]   = useState("");
  const [status, setStatus] = useState("All");
  const [agentFilter, setAgentFilter] = useState("All");
  const [sortBy, setSortBy] = useState("id_desc");
  const [alertFilter, setAlertFilter] = useState("All");
  const [selectMode, setSelectMode]   = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showCreate, setShowCreate]   = useState(false);
  const [showWallet, setShowWallet]   = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [createForm, setCreateForm]   = useState({ name: "", phone: "" });
  const [walletForm, setWalletForm]   = useState({ mode: "deposit", amount: "", remark: "" });

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      const [uRes, aRes] = await Promise.all([
        API.get("/admin/users"),
        API.get("/admin/agents").catch(() => ({ data: { agents: [] } })),
      ]);
      setUsers(uRes.data);
      setAgents(Array.isArray(aRes.data?.agents) ? aRes.data.agents : []);
    } catch (e) { console.log(e); }
  };

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = users.filter((u) => {
      if (isDemoUser(u)) return false;
      if (status !== "All" && (u.status || "Active") !== status) return false;
      if (agentFilter !== "All") {
        if (agentFilter === "Direct" && u.managed_by_agent) return false;
        if (agentFilter !== "Direct" && String(u.managed_by_agent || "") !== agentFilter) return false;
      }
      if (alertFilter === "ON"  && !areAlertsOn(u)) return false;
      if (alertFilter === "OFF" &&  areAlertsOn(u)) return false;
      if (q && ![u.id, u.name, u.phone, u.referral_code, u.fallback_login_code]
        .some((v) => String(v || "").toLowerCase().includes(q))) return false;
      return true;
    });

    list.sort((a, b) => {
      switch (sortBy) {
        case "balance_desc": return +b.balance - +a.balance;
        case "balance_asc":  return +a.balance - +b.balance;
        case "name_asc":     return (a.name||"").localeCompare(b.name||"");
        case "name_desc":    return (b.name||"").localeCompare(a.name||"");
        case "id_asc":       return +a.id - +b.id;
        default:             return +b.id - +a.id;
      }
    });
    return list;
  }, [users, query, status, agentFilter, alertFilter, sortBy]);

  const blockedCount  = users.filter((u) => u.status === "Blocked").length;
  const totalBalance  = users.reduce((s, u) => s + +u.balance, 0);

  /* ── actions ── */
  const createUser = async () => {
    try {
      await API.post("/admin/users", createForm);
      setShowCreate(false); setCreateForm({ name:"", phone:"" }); loadUsers();
    } catch (e) { alert(e?.response?.data?.message || "Error"); }
  };

  const walletAction = async () => {
    try {
      if (["wrong_deposit","wrong_withdraw"].includes(walletForm.mode)) {
        const label = walletForm.mode === "wrong_deposit" ? "deposit" : "withdraw";
        if (!window.confirm(`Delete latest approved ${label} of Rs ${walletForm.amount}?`)) return;
        await API.post("/admin/users/wrong-transaction", {
          user_id: selectedUser.id,
          entry_type: walletForm.mode === "wrong_deposit" ? "deposit" : "withdraw",
          amount: walletForm.amount, remark: walletForm.remark,
        });
      } else {
        await API.post("/admin/users/manual-transaction", {
          user_id: selectedUser.id, mode: walletForm.mode,
          amount: walletForm.amount, remark: walletForm.remark,
        });
      }
      setShowWallet(false); loadUsers();
    } catch (e) { alert(e?.response?.data?.message || "Error"); }
  };

  const genCode = async (u) => {
    try {
      const r = await API.post(`/admin/users/${u.id}/login-code`);
      const code = String(r.data?.code || "");
      loadUsers();
      if (code && navigator.clipboard?.writeText) navigator.clipboard.writeText(code).catch(()=>{});
      alert(`Code for ${u.name || u.phone}: ${code}`);
    } catch (e) { alert(e?.response?.data?.message || "Error"); }
  };

  const toggleBlock = async (u) => {
    await API.put(`/admin/users/${u.id}`, { status: u.status === "Blocked" ? "Active" : "Blocked" });
    loadUsers();
  };

  const toggleAlerts = async (u) => {
    const next = areAlertsOn(u) ? 0 : 1;
    setUsers((cur) => cur.map((x) => x.id === u.id ? { ...x, result_notifications_enabled: next } : x));
    try { await API.put(`/admin/users/${u.id}`, { result_notifications_enabled: next }); }
    catch { loadUsers(); }
  };

  const exportCsv = () => {
    const rows = [["ID","Name","Mobile","Balance","Status","Joined"],
      ...filteredUsers.map((u) => [u.id, u.name||"User", u.phone||"", +u.balance, u.status||"Active", fmtDate(u.created_at)])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g,'""')}"`).join(",")).join("\n");
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(new Blob([csv],{type:"text/csv"})), download:"users.csv" });
    a.click();
  };

  return (
    <AdminLayout>
      <div style={page}>

        {/* ── top bar ── */}
        <div style={topBar}>
          <div>
            <h1 style={pageTitle}>Users</h1>
            <p style={pageSub}>{filteredUsers.length} of {users.length} users</p>
          </div>
          <div style={topActions}>
            <button style={ghostBtn} onClick={() => { setSelectMode(v=>!v); setSelectedIds(new Set()); }}>
              <Check size={13}/> {selectMode ? `${selectedIds.size} sel.` : "Select"}
            </button>
            <button style={ghostBtn} onClick={exportCsv}><Download size={13}/></button>
            <button style={addBtn} onClick={() => setShowCreate(true)}><UserPlus size={14}/> Add</button>
          </div>
        </div>

        {/* ── 3 stat pills ── */}
        <div style={statStrip}>
          <div style={statPill("#4f46e5")}>
            <Users size={16} color="#fff"/> <span style={spVal}>{users.length}</span> <span style={spLbl}>Total</span>
          </div>
          <div style={statPill("#dc2626")}>
            <UserX size={16} color="#fff"/> <span style={spVal}>{blockedCount}</span> <span style={spLbl}>Blocked</span>
          </div>
          <div style={statPill("#f97316")}>
            <Wallet size={16} color="#fff"/> <span style={spVal}>{fmtMoney(totalBalance)}</span> <span style={spLbl}>Balance</span>
          </div>
        </div>

        {/* ── search + filters ── */}
        <div style={filterBar}>
          <div style={searchBox}>
            <Search size={14} color={C.slate}/>
            <input style={searchInput} placeholder="Name, mobile, ID…"
              value={query} onChange={(e)=>setQuery(e.target.value)}/>
          </div>
          <div style={filterRow}>
            <Sel label="Status" value={status} onChange={setStatus} opts={["All","Active","Blocked"]}/>
            <Sel label="Agent"  value={agentFilter} onChange={setAgentFilter}
              opts={["All","Direct",...agents.map((a)=>({v:a.id,l:a.name}))]}/>
            <Sel label="Sort"   value={sortBy} onChange={setSortBy} opts={[
              {v:"id_desc",l:"Latest"},
              {v:"id_asc",l:"Oldest"},
              {v:"balance_desc",l:"Top Bal"},
              {v:"balance_asc",l:"Low Bal"},
              {v:"name_asc",l:"A→Z"},
              {v:"name_desc",l:"Z→A"},
            ]}/>
            <Sel label="Alerts" value={alertFilter} onChange={setAlertFilter} opts={["All","ON","OFF"]}/>
            <button style={iconBtn} onClick={loadUsers}><RefreshCw size={13}/></button>
          </div>
        </div>

        {/* ── user card grid ── */}
        <div style={cardGrid}>
          {filteredUsers.map((u, idx) => (
            <UserCard key={u.id||idx} user={u} idx={idx} selectMode={selectMode}
              selected={selectedIds.has(u.id)}
              onSelect={() => setSelectedIds((cur)=>{ const s=new Set(cur); s.has(u.id)?s.delete(u.id):s.add(u.id); return s; })}
              onProfile={() => navigate(`/admin/users/${u.id}`)}
              onWallet={() => { setSelectedUser(u); setWalletForm({mode:"deposit",amount:"",remark:""}); setShowWallet(true); }}
              onCode={() => genCode(u)}
              onBlock={() => toggleBlock(u)}
              onAlerts={() => toggleAlerts(u)}
            />
          ))}
          {!filteredUsers.length && <div style={empty}>No users found</div>}
        </div>

        {/* ── Create modal ── */}
        {showCreate && (
          <Modal title="New User" onClose={()=>setShowCreate(false)}>
            <label style={lbl}>Name</label>
            <input style={inp} placeholder="Enter name" value={createForm.name} onChange={(e)=>setCreateForm(c=>({...c,name:e.target.value}))}/>
            <label style={lbl}>Mobile</label>
            <input style={inp} placeholder="10-digit mobile" value={createForm.phone} onChange={(e)=>setCreateForm(c=>({...c,phone:e.target.value}))}/>
            <div style={mFoot}>
              <button style={cancelBtn} onClick={()=>setShowCreate(false)}>Cancel</button>
              <button style={submitBtn("#10b981")} onClick={createUser}><UserPlus size={15}/> Create</button>
            </div>
          </Modal>
        )}

        {/* ── Wallet modal ── */}
        {showWallet && selectedUser && (
          <Modal title="Deposit / Withdraw" onClose={()=>setShowWallet(false)}>
            {/* user strip */}
            <div style={wUserStrip}>
              <div style={wAvatar(selectedUser, 0)}>{getInitial(selectedUser.name)}</div>
              <div>
                <div style={{fontWeight:900,color:C.dark2}}>{selectedUser.name}</div>
                <div style={{fontSize:12,color:C.slate,marginTop:2}}>{selectedUser.phone}</div>
              </div>
              <div style={{marginLeft:"auto",textAlign:"right"}}>
                <div style={{fontSize:11,color:C.slate}}>Balance</div>
                <div style={{fontWeight:900,color:C.dark2}}>{fmtMoney(selectedUser.balance)}</div>
              </div>
            </div>

            {/* mode toggle */}
            <div style={modeRow}>
              {[["deposit","Deposit","#10b981"],[  "withdraw","Withdraw","#ef4444"]].map(([m,label,col])=>(
                <button key={m} style={modeBtn(walletForm.mode===m,col)} onClick={()=>setWalletForm(c=>({...c,mode:m}))}>
                  {m==="deposit"?<TrendingDown size={15}/>:<TrendingUp size={15}/>} {label}
                </button>
              ))}
            </div>

            <label style={lbl}>Amount (Rs)</label>
            <input style={inp} placeholder="Enter amount" value={walletForm.amount}
              onChange={(e)=>setWalletForm(c=>({...c,amount:e.target.value}))}/>
            <div style={quickAmts}>
              {["200","500","1000","5000","10000"].map((v)=>(
                <button key={v} style={qBtn} onClick={()=>setWalletForm(c=>({...c,amount:v}))}>{v==="1000"?"1K":v==="5000"?"5K":v==="10000"?"10K":v}</button>
              ))}
            </div>

            <label style={lbl}>Remark</label>
            <input style={inp} placeholder="Optional note" value={walletForm.remark}
              onChange={(e)=>setWalletForm(c=>({...c,remark:e.target.value}))}/>
            <div style={mFoot}>
              <button style={cancelBtn} onClick={()=>setShowWallet(false)}>Cancel</button>
              <button style={submitBtn(walletForm.mode==="deposit"?"#10b981":"#ef4444")} onClick={walletAction}>
                {walletForm.mode==="deposit"?"Deposit":"Withdraw"}
              </button>
            </div>
          </Modal>
        )}
      </div>
    </AdminLayout>
  );
}

/* ─────────────── UserCard ─────────────── */

function UserCard({ user, idx, selectMode, selected, onSelect, onProfile, onWallet, onCode, onBlock, onAlerts }) {
  const [a, b] = AVATAR_COLORS[idx % AVATAR_COLORS.length];
  const isBlocked = user.status === "Blocked";
  const online    = getSessionLabel(user) !== "Offline";
  const alertsOn  = areAlertsOn(user);

  return (
    <article style={card(isBlocked)}>
      {/* ── header row ── */}
      <div style={cardHead}>
        {selectMode && (
          <input type="checkbox" checked={selected} onChange={onSelect} style={chk}/>
        )}

        {/* avatar */}
        <div style={avatarWrap}>
          {user.profile_image
            ? <img src={getMediaUrl(user.profile_image)} alt="" style={avatarImg}/>
            : <div style={avatarInit(a, b)}>{getInitial(user.name, idx)}</div>}
          <div style={onlineDot(online)}/>
        </div>

        {/* name + meta */}
        <div style={cardMeta}>
          <div style={cardName}>{user.name || "User"}</div>
          <div style={cardPhone}>{user.phone || "—"}</div>
          <div style={cardId}>ID {user.id}</div>
        </div>

        {/* status badge */}
        <div style={statusBadge(isBlocked)}>{isBlocked ? "Blocked" : "Active"}</div>
      </div>

      {/* ── balance banner ── */}
      <div style={balBanner}>
        <div>
          <div style={balLabel}>Balance</div>
          <div style={balAmt}>{fmtMoney(user.balance)}</div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={balLabel}>Session</div>
          <div style={sessionChip(user)}>{getSessionLabel(user)}</div>
        </div>
      </div>

      {/* ── info grid ── */}
      <div style={infoGrid}>
        <div style={infoCell}>
          <span style={infoKey}>Agent</span>
          <span style={infoVal}>{user.agent || "Direct"}</span>
        </div>
        <div style={infoCell}>
          <span style={infoKey}>Ref Code</span>
          <span style={{...infoVal, color:C.brand, fontWeight:800}}>{user.referral_code || "—"}</span>
        </div>
        <div style={infoCell}>
          <span style={infoKey}>Joined</span>
          <span style={infoVal}>{fmtDate(user.created_at)}</span>
        </div>
        <div style={infoCell}>
          <span style={infoKey}>Last Login</span>
          <span style={infoVal}>{fmtDateTime(user.last_login)}</span>
        </div>
      </div>

      {/* ── actions ── */}
      <div style={actBar}>
        <Btn color="#4f46e5" bg="#eef2ff" tip="Profile"    onClick={onProfile}><Eye size={14}/></Btn>
        <Btn color="#2563eb" bg="#eff6ff" tip="Wallet"     onClick={onWallet}><Wallet size={14}/></Btn>
        <button style={alertToggle(alertsOn)} onClick={onAlerts}>
          {alertsOn ? <Bell size={13}/> : <BellOff size={13}/>}
          <span>{alertsOn?"ON":"OFF"}</span>
        </button>
        <button style={blockToggle(isBlocked)} onClick={onBlock}>
          <UserX size={14}/> <span>{isBlocked?"Unblock":"Block"}</span>
        </button>
      </div>
    </article>
  );
}

/* ─── sub-components ─── */

function Btn({ color, bg, tip, onClick, children }) {
  return (
    <button title={tip} style={iconAction(bg,color)} onClick={onClick}>{children}</button>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={overlay} onClick={onClose}>
      <div style={modalBox} onClick={(e)=>e.stopPropagation()}>
        <div style={mHead}>
          <span style={mTitle}>{title}</span>
          <button style={mClose} onClick={onClose}>×</button>
        </div>
        <div style={mBody}>{children}</div>
      </div>
    </div>
  );
}

function Sel({ label, value, onChange, opts }) {
  return (
    <div style={selWrap}>
      <select style={selEl}
        value={value}
        onChange={(e)=>onChange(e.target.value)}>
        {opts.map((o) => typeof o === "string"
          ? <option key={o} value={o}>{o}</option>
          : <option key={o.v ?? o} value={o.v ?? o}>{o.l ?? o.v ?? o}</option>)}
      </select>
    </div>
  );
}

/* ─── helpers ─── */

function getInitial(name="", idx=0) {
  if (name) return name.trim().charAt(0).toUpperCase();
  return String.fromCharCode(65 + (idx % 26));
}

function isDemoUser(u={}) {
  const t = String(u.tag||"").toLowerCase();
  return t==="test"||t==="demo id"||t==="demo"||t.includes("demo");
}

function areAlertsOn(u={}) { return Number(u.result_notifications_enabled ?? 1) !== 0; }

function getSessionLabel(u={}) {
  const app = Number(u.has_app_session||0)>0;
  const web = Number(u.has_web_session||0)>0;
  if (app&&web) return "App+Web";
  if (app) return "App";
  if (web) return "Web";
  return "Offline";
}

function fmtMoney(v) {
  const n = +v||0;
  if (Math.abs(n)>=100000) return `₹${(n/100000).toFixed(1)}L`;
  return `₹${n.toLocaleString("en-IN")}`;
}

function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d) ? "—" : d.toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"2-digit"});
}

function fmtDateTime(v) {
  if (!v) return "Never";
  const d = new Date(v);
  return isNaN(d) ? "Never" : d.toLocaleString("en-IN",{day:"2-digit",month:"short",year:"2-digit",hour:"2-digit",minute:"2-digit"});
}

/* ─── dynamic styles ─── */

function statPill(col) {
  return { display:"flex", alignItems:"center", gap:8, background:col, borderRadius:14, padding:"10px 18px", boxShadow:`0 6px 18px ${col}44` };
}

function card(blocked) {
  return {
    background: "#fff",
    borderRadius: 20,
    border: blocked ? "1.5px solid #fecaca" : "1.5px solid #e8eaf6",
    boxShadow: "0 4px 20px rgba(79,70,229,0.07)",
    display: "grid",
    gap: 12,
    padding: 16,
    overflow: "hidden",
    transition: "box-shadow .15s",
  };
}

function avatarInit(a, b) {
  return {
    width:48, height:48, borderRadius:"50%",
    background:`linear-gradient(135deg,${a},${b})`,
    display:"grid", placeItems:"center",
    fontSize:20, fontWeight:900, color:"#fff",
  };
}

function onlineDot(online) {
  return {
    position:"absolute", right:0, bottom:0,
    width:11, height:11, borderRadius:"50%",
    border:"2px solid #fff",
    background: online ? "#22c55e" : "#94a3b8",
  };
}

function sessionChip(u) {
  const label = getSessionLabel(u);
  const map = { "App+Web":["#ede9fe","#6d28d9"], App:["#dcfce7","#15803d"], Web:["#eff6ff","#2563eb"], Offline:["#f1f5f9","#64748b"] };
  const [bg,col] = map[label]||map.Offline;
  return { display:"inline-block", padding:"2px 8px", borderRadius:999, background:bg, color:col, fontSize:10, fontWeight:800 };
}

function statusBadge(blocked) {
  return {
    display:"inline-flex", alignItems:"center", gap:5,
    padding:"5px 12px", borderRadius:999,
    background: blocked ? "#fee2e2" : "#dcfce7",
    color: blocked ? C.red : C.green,
    fontWeight:800, fontSize:11, whiteSpace:"nowrap",
    flexShrink:0,
  };
}

function alertToggle(on) {
  return {
    display:"inline-flex", alignItems:"center", gap:5,
    height:30, padding:"0 10px", borderRadius:999,
    border:`1px solid ${on?"#bbf7d0":"#fecaca"}`,
    background: on?"#ecfdf5":"#fff1f2",
    color: on?C.green:C.red,
    fontWeight:900, fontSize:11, cursor:"pointer", whiteSpace:"nowrap",
  };
}

function blockToggle(blocked) {
  return {
    display:"inline-flex", alignItems:"center", gap:5,
    height:30, padding:"0 10px", borderRadius:999,
    border:`1px solid ${blocked?"#bbf7d0":"#fecaca"}`,
    background: blocked?"#ecfdf5":"#fff1f2",
    color: blocked?C.green:C.red,
    fontWeight:900, fontSize:11, cursor:"pointer",
  };
}

function iconAction(bg, col) {
  return { width:32, height:32, borderRadius:10, border:"none", background:bg, color:col, display:"grid", placeItems:"center", cursor:"pointer" };
}

function modeBtn(active, col) {
  return {
    flex:1, height:44, border:0, borderRadius:12,
    background: active ? col : "#f1f5f9",
    color: active ? "#fff" : "#64748b",
    fontWeight:800, fontSize:14,
    display:"flex", alignItems:"center", justifyContent:"center", gap:7,
    cursor:"pointer",
    boxShadow: active ? `0 6px 16px ${col}44` : "none",
  };
}

function submitBtn(col) {
  return {
    flex:1, height:50, border:0, borderRadius:14,
    background:col, color:"#fff",
    fontWeight:900, fontSize:15,
    display:"flex", alignItems:"center", justifyContent:"center", gap:8,
    cursor:"pointer",
  };
}

function wAvatar(u, idx) {
  const [a,b] = AVATAR_COLORS[idx % AVATAR_COLORS.length];
  return {
    width:44, height:44, borderRadius:"50%",
    background:`linear-gradient(135deg,${a},${b})`,
    display:"grid", placeItems:"center",
    fontSize:18, fontWeight:900, color:"#fff", flexShrink:0,
  };
}

/* ─── static styles ─── */
const page       = { display:"grid", gap:20, fontFamily:"'Segoe UI',Inter,system-ui,sans-serif" };
const topBar     = { display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 };
const pageTitle  = { margin:0, fontSize:26, fontWeight:900, color:C.dark2, letterSpacing:"-0.5px" };
const pageSub    = { margin:"3px 0 0", color:C.slate, fontSize:12 };
const topActions = { display:"flex", gap:8, alignItems:"center" };
const ghostBtn   = { height:34, padding:"0 14px", display:"flex", alignItems:"center", gap:6, border:`1.5px solid #e2e8f0`, background:"#fff", color:"#334155", fontWeight:700, fontSize:12, cursor:"pointer", borderRadius:10 };
const addBtn     = { height:34, padding:"0 16px", display:"flex", alignItems:"center", gap:7, border:0, background:`linear-gradient(135deg,${C.brand},${C.brand2})`, color:"#fff", fontWeight:700, fontSize:12, borderRadius:10, boxShadow:`0 4px 14px ${C.brand}44`, cursor:"pointer" };
const statStrip  = { display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 };
const spVal      = { fontSize:22, fontWeight:900, color:"#fff", lineHeight:1 };
const spLbl      = { fontSize:11, color:"rgba(255,255,255,0.7)", fontWeight:600 };
const filterBar  = { display:"flex", flexWrap:"wrap", gap:10, alignItems:"center" };
const searchBox  = { height:40, flex:"1 1 220px", display:"flex", alignItems:"center", gap:9, padding:"0 13px", background:"#fff", border:`1.5px solid #e2e8f0`, borderRadius:12 };
const searchInput= { flex:1, border:0, outline:0, fontSize:13, color:"#334155", background:"transparent" };
const filterRow  = { display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" };
const selWrap    = { height:38, display:"flex", alignItems:"center", padding:"0 10px", background:"#fff", border:`1.5px solid #e2e8f0`, borderRadius:10 };
const selEl      = { border:0, outline:0, background:"transparent", fontSize:12, color:"#334155", cursor:"pointer" };
const iconBtn    = { width:38, height:38, display:"grid", placeItems:"center", border:`1.5px solid #e2e8f0`, background:"#fff", color:C.brand, cursor:"pointer", borderRadius:10 };
const cardGrid   = { display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:16 };
const cardHead   = { display:"flex", alignItems:"center", gap:12 };
const avatarWrap = { position:"relative", flexShrink:0 };
const avatarImg  = { width:48, height:48, borderRadius:"50%", objectFit:"cover", display:"block" };
const cardMeta   = { flex:1, minWidth:0 };
const cardName   = { fontSize:15, fontWeight:900, color:C.dark2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" };
const cardPhone  = { fontSize:12, color:C.slate, marginTop:2 };
const cardId     = { fontSize:10, color:"#94a3b8", fontWeight:700, marginTop:1 };
const balBanner  = { background:`linear-gradient(135deg,${C.dark2},#312e81)`, borderRadius:14, padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center" };
const balLabel   = { fontSize:10, color:"rgba(255,255,255,0.55)", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:3 };
const balAmt     = { fontSize:18, fontWeight:900, color:"#fff" };
const infoGrid   = { display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 };
const infoCell   = { display:"flex", flexDirection:"column", gap:2, background:"#f8f9ff", borderRadius:10, padding:"8px 10px" };
const infoKey    = { fontSize:9, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.06em" };
const infoVal    = { fontSize:12, fontWeight:700, color:C.dark, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" };
const codePill   = { display:"inline-block", background:"#fef3c7", border:"1px solid #fde68a", borderRadius:999, padding:"3px 10px", fontSize:11, fontWeight:900, color:"#92400e" };
const actBar     = { display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", borderTop:`1px solid #f1f5f9`, paddingTop:12 };
const chk        = { width:16, height:16, accentColor:C.brand, flexShrink:0 };
const empty      = { gridColumn:"1/-1", padding:48, textAlign:"center", color:"#94a3b8", fontSize:14 };

/* modal */
const overlay  = { position:"fixed", inset:0, background:"rgba(15,23,42,0.52)", display:"grid", placeItems:"center", zIndex:1000 };
const modalBox = { width:500, maxWidth:"94vw", background:"#fff", borderRadius:24, overflow:"hidden", boxShadow:"0 32px 80px rgba(15,23,42,0.28)" };
const mHead    = { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"22px 24px 16px", background:"linear-gradient(135deg,#f8f9ff,#eef2ff)", borderBottom:`1.5px solid #e8eaf6` };
const mTitle   = { fontSize:19, fontWeight:900, color:C.dark2 };
const mClose   = { border:0, background:"transparent", fontSize:26, color:"#94a3b8", cursor:"pointer", lineHeight:1 };
const mBody    = { padding:24, display:"grid", gap:12 };
const lbl      = { fontSize:13, fontWeight:700, color:"#334155" };
const inp      = { height:50, border:`1.5px solid #e2e8f0`, borderRadius:14, padding:"0 16px", fontSize:15, outline:0, boxSizing:"border-box" };
const mFoot    = { display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:4 };
const cancelBtn= { height:50, border:`1.5px solid #e2e8f0`, background:"#fff", borderRadius:14, fontSize:15, fontWeight:700, cursor:"pointer", color:"#334155" };
const wUserStrip={ display:"flex", alignItems:"center", gap:12, background:"#f8f9ff", borderRadius:16, padding:"12px 14px" };
const modeRow  = { display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, background:"#f3f4f6", padding:4, borderRadius:14 };
const quickAmts= { display:"flex", gap:6, flexWrap:"wrap" };
const qBtn     = { height:36, padding:"0 12px", border:0, background:"#f1f5f9", borderRadius:10, fontWeight:700, fontSize:12, color:"#334155", cursor:"pointer" };

export default AdminUsers;
